/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import studioWorkspaceIdQuery, {
	type StudioWorkspaceIdLoaderQuery,
} from '@jira/project-pages__contextual-summary/src/ui/agentic-automations-banner/get-studio-workspace-id/__generated__/StudioWorkspaceIdLoaderQuery.graphql';
import { createAri } from '@jira/platform__utils-ari/src/index.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';

import parameters from './__generated__/JiraSoftwareSummaryAppQuery$parameters.ts';
import type { JiraSoftwareSummaryAppQuery } from './__generated__/JiraSoftwareSummaryAppQuery.graphql.ts';

type SummarySoftwareEntrypointComponent = EntryPointComponent<
	{
		summaryQuery: JiraSoftwareSummaryAppQuery;
		studioWorkspaceIdQuery?: StudioWorkspaceIdLoaderQuery;
	},
	{},
	{},
	{}
>;

// Copied from jira/src/packages/platform/marketplace-coding-agents/src/getAvailableMarketplaceAgents.tsx
// Entrypoints cannot import that module (@atlassian/react-entrypoint/no-imports-in-entrypoint-file).
// Keep this list identical in summarySoftwareEntrypoint and summaryBusinessEntrypoint.
const getEcosystemAppIds = (environment: string | null | undefined): string[] => {
	if (environment === 'staging') {
		return [
			// githubCopilotAgent
			createAri({
				resourceOwner: 'ecosystem',
				resourceType: 'app',
				resourceId: 'cb576289-bcf1-4a78-9737-ac31bba1ff4c',
			}),
			// cursorAgent
			createAri({
				resourceOwner: 'ecosystem',
				resourceType: 'app',
				resourceId: '11995cbf-629c-4d90-890c-7fdd0ed69c46',
			}),
			// claudeAgent
			createAri({
				resourceOwner: 'ecosystem',
				resourceType: 'app',
				resourceId: '326a35e4-bcbf-4c3e-886b-7e34b32f0c1b',
			}),
		];
	}

	return [
		// githubCopilotAgent
		createAri({
			resourceOwner: 'ecosystem',
			resourceType: 'app',
			resourceId: '90b44d4a-14b7-4501-bd2c-5fe4a3fbc4e0',
		}),
		// cursorAgent
		createAri({
			resourceOwner: 'ecosystem',
			resourceType: 'app',
			resourceId: '094945a8-4d32-4f0f-8ab9-76245e467a82',
		}),
		// claudeAgent
		createAri({
			resourceOwner: 'ecosystem',
			resourceType: 'app',
			resourceId: '60add9ed-404d-42e8-8301-b659329d4ad0',
		}),
	];
};

export const summarySoftwareEntrypoint: EntryPoint<
	SummarySoftwareEntrypointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<SummarySoftwareEntrypointComponent>(
		/* webpackChunkName: "async-summary-software-entrypoint" */ './JiraSoftwareSummaryApp.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: ({
		context,
		tenantContext: { cloudId, environment },
	}: EntryPointRouteParams) => {
		const projectKey = context.match.params.projectKey;

		if (projectKey == null) {
			throw new Error('Project key missing');
		}

		const ecosystemAppIds = expValEquals('jira_agentic_summary_view', 'isEnabled', true, false)
			? getEcosystemAppIds(environment)
			: [];

		return {
			queries: {
				summaryQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
						networkCacheConfig: {
							metadata: { META_SLOW_ENDPOINT: true },
						},
					},
					parameters,
					variables: {
						cloudId,
						// Only needed when agentic summary (and codeProviders fragment) is included.
						cloudAri: expValEquals('jira_agentic_summary_view', 'isEnabled', true, false)
							? createAri({
									resourceOwner: 'jira',
									resourceType: 'site',
									resourceId: cloudId,
								})
							: '',
						projectKey,
						scope: { project: { projectKey } },
						isFilterRefinementEnabled: expValEquals(
							'jira_add_harmonisation_filter_to_summary_view',
							'isEnabled',
							true,
							false,
						),
						isAgenticSummaryViewEnabled: expValEquals(
							'jira_agentic_summary_view',
							'isEnabled',
							true,
							false,
						),
						installStatusContextAri: expValEquals(
							'jira_agentic_summary_view',
							'isEnabled',
							true,
							false,
						)
							? createAri({
									resourceOwner: 'jira',
									resourceType: 'site',
									resourceId: cloudId,
								})
							: '',
						ecosystemAppIds,
						pulseIntakeIssueSearchInput: {
							jql: `project = "${projectKey}" AND hierarchyLevel = 0 AND created >= -7d`,
						},
						pulseTriageIssueSearchInput: {
							jql: `project = "${projectKey}" AND hierarchyLevel = 0 AND statusCategory = "To Do"`,
						},
						pulseExecuteIssueSearchInput: {
							jql: `project = "${projectKey}" AND hierarchyLevel = 0 AND statusCategory = "In Progress"`,
						},
						pulseDeliveryIssueSearchInput: {
							jql: `project = "${projectKey}" AND hierarchyLevel = 0 AND statusCategory = "Done" AND statusCategoryChangedDate >= -7d`,
						},
						boundedProjectIssueSearchInput: {
							jql: `project = "${projectKey}" AND hierarchyLevel = 0`,
						},
						pulseIntakeBadgeIssueSearchInput: {
							jql: `project = "${projectKey}" AND statusCategory = "To Do" AND sprint is EMPTY AND (description is EMPTY OR updated <= "-12w")`,
						},
						pulseTriageBadgePrimaryIssueSearchInput: {
							jql: `project = "${projectKey}" AND resolution is EMPTY AND (due < now() OR (sprint is EMPTY AND due <= endOfDay("+14d")) OR (sprint is not EMPTY AND due <= endOfDay("+7d")))`,
						},
						pulseTriageBadgeFallbackIssueSearchInput: {
							jql: `project = "${projectKey}" AND statusCategory = "To Do" AND description is not EMPTY AND (sprint is EMPTY OR due is EMPTY)`,
						},
						pulseExecuteBadgePrimaryIssueSearchInput: {
							jql: `project = "${projectKey}" AND (deploymentState = "failed" OR buildState = "failed")`,
						},
						pulseExecuteBadgeFallbackIssueSearchInput: {
							jql: `project = "${projectKey}" AND "Agent Sessions"[state] = "AWAITING_INPUT" AND statusCategory = "In Progress"`,
						},
						pulseDeliveryBadgePrimaryIssueSearchInput: {
							jql: `project = "${projectKey}" AND resolution is EMPTY AND (issueLinkType in ("is blocked by", "Blocks") OR flagged = Impediment OR (sprint in openSprints() AND status = "In Progress" AND updated <= "-7d") OR (sprint is EMPTY AND statusCategory = "In Progress" AND updated <= "-14d"))`,
						},
						pulseDeliveryBadgeFallbackIssueSearchInput: {
							jql: `project = "${projectKey}" AND (statusCategory = "In Progress" OR assignee is not EMPTY OR sprint is not EMPTY) AND updated <= "-7d"`,
						},
					},
				},
				...(fg('jira_agentic_automations_kill_switch')
					? {
							studioWorkspaceIdQuery: {
								options: {
									fetchPolicy: 'store-or-network' as const,
								},
								parameters: studioWorkspaceIdQuery,
								variables: {
									jiraSiteAri: `ari:cloud:jira::site/${cloudId}`,
								},
							},
						}
					: {}),
			},
		};
	},
});
